import React, { createContext, useState, useEffect, useMemo } from "react";
import { useLocation } from "react-router-dom";
import { Axios, clearAllRedirect, getNextFifteen, getSid, getViewportWidth } from "./utils";
import { baseApi, sizes } from "./constants";
import type { AdminConfig, AppContext, Config, ContextValue, Size } from "../types";

const configTransform = (v: Config): Config => JSON.parse(JSON.stringify(v).replace(/\|URL_PREFIX\|/g, `/`));

export const Context = createContext<ContextValue>({} as ContextValue);
const iframe = window.self !== window.top;

const calcSize = (): Size => {
    let output: Size = `large`;
    const vw = getViewportWidth();
    sizes.forEach(([min, max, nm]) => {
        if (vw > min && vw < max) {
            output = nm;
        }
    });
    return output;
};

let redrawing: ReturnType<typeof setTimeout> | null | false = null;
const resize = (
    setSize: React.Dispatch<React.SetStateAction<Size>>,
    setLastResized: React.Dispatch<React.SetStateAction<number>>,
    setViewportWidth: React.Dispatch<React.SetStateAction<number>>
) => {
    clearTimeout(redrawing as ReturnType<typeof setTimeout>);
    redrawing = setTimeout(() => {
        window.requestAnimationFrame(() => {
            clearTimeout(redrawing as ReturnType<typeof setTimeout>);
            redrawing = false;
            setLastResized(Date.now());
            setSize(calcSize());
            setViewportWidth(getViewportWidth());
        });
    }, 55);
};

export const ContextProvider = ({ children }: { children: React.ReactNode }) => {
    const adminConfig: AdminConfig = localStorage.getItem(`adminConfig`)
        ? JSON.parse(localStorage.getItem(`adminConfig`) as string)
        : {};
    const config: Config = localStorage.getItem(`config`) ? JSON.parse(localStorage.getItem(`config`) as string) : {};

    const [context, sc] = useState<AppContext>({
        user: {},
        adminConfig,
        config,
        programGuid: localStorage.getItem(`programGuid`),
    });
    const [loaded, setLoaded] = useState(false);
    const [axiosIsReady, setAxiosIsReady] = useState(false);
    const [viewportWidth, setViewportWidth] = useState(getViewportWidth());
    const [loading, setLoading] = useState(false);
    const [size, setSize] = useState<Size>(`large`);
    const [lastResized, setLastResized] = useState(Date.now());
    const [Lastadminconfigupdate, setLastadminconfigupdate] = useState<string | undefined>();
    const setContext = (nv: Partial<Config>) => sc({ ...context, config: { ...context.config, ...nv } });
    const getValue = (key?: string) => (key ? context?.[key as keyof AppContext] : context);
    const location = useLocation();

    const refreshContext = (): Promise<void> =>
        Axios.get<{
            config: Config;
            adminConfig: AdminConfig;
            programGuid: string;
            holidays: string[];
            post?: string;
        }>(`${baseApi}config?host=${btoa(window.location.host)}${iframe ? `&iframe=1` : ``}`).then(r => {
            const liBxInstalledData = sessionStorage.getItem(`liBxInstalledData`);
            const { data } = r;
            data.config = configTransform(data.config);
            sc({
                ...context,
                ...data,
                liBxInstalledData,
                nextFifteen: getNextFifteen(
                    data?.holidays.map(d => new Date(d)),
                    data.adminConfig
                ),
            });
            if (getSid()) {
                setLoaded(true);
            }
            if (data.adminConfig) {
                localStorage.setItem(`adminConfig`, JSON.stringify(data.adminConfig));
            }
            if (data.config) {
                localStorage.setItem(`config`, JSON.stringify(data.config));
            }
            if (data.programGuid) {
                localStorage.setItem(`programGuid`, data.programGuid);
            }
            if (data.post) {
                window.parent.postMessage({ action: `signedIn`, resp: data.post }, `*`);
            }
        });

    useEffect(() => {
        Axios.interceptors.response.use(
            // Any status code that lie within the range of 2xx cause this function to trigger
            // Do something with response data
            response => {
                if (response?.headers?.pragma && Lastadminconfigupdate !== response.headers.pragma) {
                    if (Lastadminconfigupdate) {
                        refreshContext();
                    }
                    setLastadminconfigupdate(response.headers.pragma);
                }
                if (response.headers?.liauthorization) {
                    localStorage.setItem(`sid`, response.headers.liauthorization);
                    if (sessionStorage.getItem(`edit`)) {
                        sessionStorage.setItem(`sid`, response.headers.liauthorization);
                    }
                }
                return response;
            },
            error => {
                // Any status codes that falls outside the range of 2xx cause this function to trigger
                // Do something with response error
                if (error.response && [401, 502].indexOf(error.response.status) > -1) {
                    clearAllRedirect(context);
                }
                return Promise.reject(error);
            }
        );
        Axios.interceptors.request.use(
            x => {
                const r = x;
                r.headers.Authorization = `Bearer ${getSid() || `  `}`;

                return r;
            },
            error => Promise.reject(error)
        );
        setAxiosIsReady(true);
        resize(setSize, setLastResized, setViewportWidth);
        window.addEventListener(`resize`, () => resize(setSize, setLastResized, setViewportWidth));
    }, []);

    useEffect(() => {
        if (context?.adminConfig?.mall?.Favicon) {
            const icons = document.getElementsByTagName(`link`);
            if (icons) {
                const ar = Array.prototype.slice.call(icons);
                for (let i = 0; i < ar.length; i += 1) {
                    if (ar[i][`data-litag`]) {
                        return;
                    }
                }
                const lnk = document.createElement(`link`);
                (lnk as HTMLLinkElement & Record<string, unknown>)[`data-litag`] = `favicon`;
                lnk.rel = `icon`;
                lnk.href = context?.adminConfig?.mall?.Favicon;
                document.head.appendChild(lnk);
            }
        }
    }, [context]);

    useEffect(() => {
        const sid = getSid();
        if (!loading && axiosIsReady) {
            refreshContext();
            if (sid) {
                setLoading(true);
            }
        }
    }, [location.pathname, axiosIsReady]);

    const save = (): Promise<Config> => Axios.put(`${baseApi}config`, context.config).then(r => r.data);

    const contextMemo: ContextValue = useMemo(
        () => ({
            setContext,
            save,
            user: context.user,
            context,
            viewportWidth,
            Axios,
            editable: !!sessionStorage.getItem(`edit`),

            loaded,
            getValue,
            size,
            refreshContext,
            useMobileTables: viewportWidth < 1150,
        }),
        [context, lastResized, axiosIsReady]
    );

    return <Context.Provider value={contextMemo}>{children}</Context.Provider>;
};
